import { FieldWrapper, FieldWrapperProps } from '@dixa/lib-shared-habla';
import uniqueId from 'lodash/uniqueId';
import { useState } from 'react';

import { InputTemplate } from 'components/inputTemplate';
import {
  EditorProps,
  InputProps,
  InputWrapperPicks,
} from 'components/inputTemplate/inputTemplate.types';

export type FieldTemplateProps = {
  /** The input props. */
  input: InputProps;
} & Omit<FieldWrapperProps, 'prefix' | 'defaultValue' | 'onChange' | 'name'> &
  Omit<EditorProps, 'fieldTemplateId'> &
  InputWrapperPicks;

/**
 * A component that is a custom implementation of the AceEditor.
 *
 * It wraps the input template component with the field wrapper component from
 * habla to add labels and redux-form support. The actual AceEditor
 * implementation is inside the input template component. Input template also
 * wraps the AceEditor furthermore with input wrapper from habla which adds all
 * validation and input states automatically. The implementation was split into
 * two components to be able to easily change the outer field wrapper in case we
 * want to use something else than redux-form.
 */
export const FieldTemplate = ({
  className,
  input,
  label,
  meta,
  required,
  disabled,
  helpIcon,
  description,
  noLabel,
  noAlert,
  labelFor,
  ...inputTemplateProps
}: FieldTemplateProps): JSX.Element => {
  const [fieldTemplateId] = useState<string>(
    () => labelFor || uniqueId('fieldTemplate-'),
  );

  return (
    <FieldWrapper
      className={className}
      label={label}
      labelFor={fieldTemplateId}
      meta={meta}
      required={required}
      helpIcon={helpIcon}
      description={description}
      noLabel={noLabel}
      noAlert={noAlert}
      disabled={disabled}
    >
      <InputTemplate
        status={FieldWrapper.getStatus(meta)}
        fieldTemplateId={fieldTemplateId}
        disabled={disabled}
        {...input}
        {...inputTemplateProps}
      />
    </FieldWrapper>
  );
};
